import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useCallback, useEffect, useRef, useState } from "react";
import { setColumnWidth } from "../../../helpers/cookieUtils";
import ListingHeader from "../listingHeader/ListingHeader";
import {
  Tooltip,
  TableCellLayout,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  makeStyles,
  Avatar,
} from "@fluentui/react-components";
import {
  DeleteIcon,
  EditIcon,
  FlagIcon,
  MeatBallsMenuIcon,
  PendingDeleteIcon,
  RemindIcon,
} from "../../../modules/default-svg";
import userService from "../../../store/services/user.service";
import { mfaService } from "../../../store/services/mfa.service";
import moment from "moment";
import { generalActions } from "../../../store/actions";
import Spin from "../spinner/Spin";
import {
  updateUserFlag,
  updateUserRequest,
} from "../../../store/actions/user.actions";
import HoverCard from "../../hoverCard/HoverCard";
import {
  getAccountCurrency,
  utcToLocalDateString,
  displayDecimalFormatted,
} from "../../../utils/systemSettings";
import service from "../../../store/services/user.service";
import CustomSelect from "../select/CustomSelect";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  compareArrays,
  formatSequenceNumber,
  getDeletePermission,
  getEditPermission,
  sortColumnsByOrderlist,
} from "../../../helpers/utils";
import Activate from "../../../assets/svg/Activate.svg";
import Deactivate from "../../../assets/svg/Deactivate.svg";
import {
  isValidDate,
  localDateToISODateString,
} from "../../../utils/systemSettings";
import Modal from "../modal/Modal";
import { ShieldOff } from "lucide-react";
import { createSkeletonRows } from "./utils/tableUtils";
import useNavigation from "../../../utils/useNavigation";

const EditInput = ({
  label,
  value,
  onChange,
  type,
  onBlur,
  dateFormat,
  table,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [roleOptions, setRoleOptions] = useState();
  const [dropdownOptions, setDropdownOptions] = useState([]);

  const formatValue = (val) => {
    if (!val) return null;
    if (val.label && val.value) return val;
    return { label: val.name, value: val.id };
  };

  const handleDateChange = (newDate) => {
    if (isValidDate(newDate)) {
      const newValue = localDateToISODateString(newDate);
      setInputValue(newValue);
      onChange(newValue);
    }
  };

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const creatingRowState = table.getState().creatingRow;
      if (table?.options?.onCreatingRowSave && creatingRowState) {
        const currentValues = {
          ...creatingRowState.values,
        };
        table.options.onCreatingRowSave({
          values: currentValues,
          table,
        });
      }
    }
  };

  const handleInputChange = (newValue) => {
    setInputValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  if (type === "text") {
    return (
      <input
        placeholder={`Enter ${label}`}
        value={inputValue}
        type="text"
        onChange={(e) => handleInputChange(e.target.value)}
        className="Custom-Input"
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
      />
    );
  }

  if (type === "number") {
    return (
      <input
        placeholder={`Enter ${label}`}
        value={inputValue}
        type="number"
        min={0}
        onChange={(e) => handleInputChange(e.target.value)}
        className="Custom-Input"
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
      />
    );
  }

  const searchRoleHandler = (value) => {
    if (value === "") {
      setRoleOptions([]);
      return;
    }
    service
      .searchRoleDataRequest(value)
      .then((res) => {
        const transformedArray = res.map((obj) => ({
          label: obj.name,
          value: obj.id,
        }));
        setRoleOptions(transformedArray);
      })
      .catch((err) => {
        setRoleOptions([]);
      });
  };

  const dropdownHandler = () => {
    if (label === "Type") {
      setDropdownOptions(() =>
        ["External", "Internal"].map((item) => ({
          label: item,
          value: item,
        }))
      );
    } else if (label === "Status") {
      setDropdownOptions(() =>
        ["Active", "Inactive"].map((item) => ({
          label: item,
          value: item,
        }))
      );
    }
  };

  if (type === "select") {
    let searchTextFunc = searchRoleHandler;
    let options = roleOptions;
    let selectText = "roles";
    let isCreatable = false;
    if (label === "Type") {
      searchTextFunc = dropdownHandler;
      options = dropdownOptions;
      selectText = "type";
    } else if (label === "Status") {
      searchTextFunc = dropdownHandler;
      options = dropdownOptions;
      selectText = "status";
    }

    const handleSelectChange = (item) => {
      if (!item) return;
      const newValue = { id: item.value, name: item.label };
      handleInputChange(newValue);
    };

    return (
      <div className="d-flex align-items-center w-full max-w-md mx-auto">
        <CustomSelect
          searchText={searchTextFunc}
          SelectText={selectText}
          CustomClass="ModalAvatarSearch CustomSelect"
          options={options}
          value={formatValue(value)}
          onChange={handleSelectChange}
          creatable={isCreatable}
          setCreated={handleSelectChange}
          handleBlur={onBlur}
          isDatatable
          selectWd="w-100"
          onKeyDown={handleKeyDown}
        />
      </div>
    );
  }

  return (
    <DatePicker
      selected={inputValue ? moment(inputValue).toDate() : null}
      onChange={handleDateChange}
      dateFormat={dateFormat}
      className="CustomDatePickerInput"
      onBlur={onBlur}
      popperPlacement="bottom-start"
      placeholderText={dateFormat}
      onKeyDown={handleKeyDown}
      popperClassName="CustomDatePickerPopper"
      showPopperArrow={false}
    />
  );
};

const useStyles = makeStyles({
  tooltip: {
    backgroundColor: "#342c82",
    color: "#FFFFFF",
  },
});

const ResourceTable = (props) => {
  const {
    columnsList,
    rowData,
    setIsCreate,
    searchHandler,
    tableName,
    handleColumnOrdering,
    dispatch,
    setPageNum,
    pagination,
    handleReminder,
    handleEdit,
    handleDelete,
    parentType,
    resourceLoading,
    handleAddRow,
    setRowData,
    handleRefresh,
    triggerSearch
  } = props;
  const styles = useStyles();
  const [cols, setCols] = useState([]);
  const [rows, setRows] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const setting = JSON.parse(localStorage.getItem("settings"));
  const [editedRows, setEditedRows] = useState({});
  const tableContainerRef = useRef(null);
  const lastScrollTop = useRef(0);
  const [editingHeader, setEditingHeader] = useState(null);
  const [headerValue, setHeaderValue] = useState("");
  const [columnSizing, setColumnSizing] = useState({});
  const [columnOrder, setColumnOrder] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [popoverStates, setPopoverStates] = useState({});
  const [confirmationModalData, setConfirmationModalData] = useState(null);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const { navigateTo } = useNavigation();

  const handlePopoverChange = (itemId, isOpen) => {
    setPopoverStates(isOpen ? { [itemId]: isOpen } : {});
  };
  const [colsList, setColumnsList] = useState([]);
  useEffect(() => {
    if (columnsList.length) {
      setColumnsList(columnsList);
    }
  }, [columnsList]);
  const selectRefs = useRef({});
  const handleHeaderDoubleClick = (columnId, currentHeader) => {
    setEditingHeader(columnId);
    setHeaderValue(currentHeader);
  };

  const handleHeaderSave = (columnId) => {
    if (!headerValue.trim()) {
      dispatch(
        generalActions.showToast({
          title: "Header name cannot be empty",
          type: "error",
        })
      );
      return;
    }

    const updatedColumnsDB = cols.map((col) => ({
      name: col.Label,
      customName: col.accessorKey === columnId ? headerValue : col.CustomName,
      order: col.Order,
      isVisible: col.Visibility,
    }));

    const payload = {
      Name: tableName,
      Columns: updatedColumnsDB,
    };

    userService
      .putUserLayoutRequest(payload)
      .then(() => { })
      .catch(() => {
        dispatch(
          generalActions.showToast({
            title: "Error updating column header",
            type: "error",
          })
        );
      })
      .finally(() => {
        setColumnsList((prevCols) => {
          const newCols = prevCols.map((col) =>
            col.accessorKey === columnId
              ? { ...col, header: headerValue, customName: headerValue }
              : col
          );
          return newCols;
        });
      });

    setEditingHeader(null);
    setHeaderValue("");
  };

  const handleFlag = (id, status) => {
    const newData = [...rowData];
    const index = newData.findIndex((item) => item.id === id);
    if (index !== -1) {
      newData[index].isFlagged = status;
    }

    setRowData(
      newData.sort((a, b) => {
        return b.isFlagged - a.isFlagged;
      })
    );
    dispatch(
      updateUserFlag({
        id: id,
        payload: [
          {
            op: "replace",
            path: "/IsFlagged",
            value: status,
          },
        ],
      })
    );
  };

  const fetchMoreData = useCallback(() => {
    if (!pagination?.HasNext || resourceLoading || isFetchingMore) return;
    setIsFetchingMore(true);
    setPageNum(pagination.CurrentPage + 1);
  }, [pagination, resourceLoading, isFetchingMore, setPageNum]);

  useEffect(() => {
    if (!resourceLoading && isFetchingMore) {
      setIsFetchingMore(false);
    }
  }, [resourceLoading, rowData.length]);

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement) => {
      if (!containerRefElement || resourceLoading || !pagination?.HasNext) return;

      const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
      const scrolledToBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 50;
      const isScrollingDown = scrollTop > lastScrollTop.current;

      lastScrollTop.current = scrollTop;

      if (isScrollingDown && scrolledToBottom) {
        fetchMoreData();
      }
    },
    [fetchMoreData, resourceLoading, pagination]
  );

  const throttledFetchMore = useCallback(
    (() => {
      let timeoutId;
      return (containerRefElement) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          fetchMoreOnBottomReached(containerRefElement);
        }, 100);
      };
    })(),
    [fetchMoreOnBottomReached]
  );


  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      const handleScroll = () => throttledFetchMore(container);
      container.addEventListener("scroll", handleScroll, { passive: true });
      return () => {
        container.removeEventListener("scroll", handleScroll);
      };
    }
  }, [throttledFetchMore]);

  // Reset scroll position when search is triggered
  useEffect(() => {
    if (tableContainerRef.current && triggerSearch !== undefined) {
      tableContainerRef.current.scrollTop = 0;
      lastScrollTop.current = 0;
    }
  }, [triggerSearch]);

  const formatPayload = (payload) => {
    const formattedPayload = {};
    Object.keys(payload).forEach((key) => {
      if (key === "dateCreated") {
        formattedPayload[key] = payload[key]
          ? moment(payload[key]).format("YYYY-MM-DD")
          : null;
      } else {
        formattedPayload[key] = payload[key];
      }
    });
    return formattedPayload;
  };

  const handleSave = (rowId, payload) => {
    dispatch(
      updateUserRequest({
        id: rowId,
        payload,
      })
    );
    setEditedRows({});
  };

  // const handleCreate = (payload) => dispatch(createUserRequest({ payload }));

  const getSortingFunction = (col) => {
    switch (col.label) {
      case "Name":
        return (rowA, rowB, columnId) => {
          const a = (rowA.original.fullName || "").toLowerCase();
          const b = (rowB.original.fullName || "").toLowerCase();
          return a.localeCompare(b);
        };
      case "Sequence #":
        return (rowA, rowB, columnId) => {
          const a = rowA.original.sequenceNumber || 0;
          const b = rowB.original.sequenceNumber || 0;
          return a - b;
        };
      case "Clients":
        return (rowA, rowB, columnId) => {
          const a = (rowA.original.clients?.[0]?.name || "").toLowerCase();
          const b = (rowB.original.clients?.[0]?.name || "").toLowerCase();
          return a.localeCompare(b);
        };
      case "Email":
        return (rowA, rowB, columnId) => {
          const a = (rowA.original.email || "").toLowerCase();
          const b = (rowB.original.email || "").toLowerCase();
          return a.localeCompare(b);
        };

      case "Title":
        return (rowA, rowB, columnId) => {
          const a = (rowA.original.title || "").toLowerCase();
          const b = (rowB.original.title || "").toLowerCase();
          return a.localeCompare(b);
        };

      case "Hourly Cost Rate":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.hourlyRate) || 0;
          const b = parseFloat(rowB.original.hourlyRate) || 0;
          return a - b;
        };

      case "Hourly Bill Rate":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.hourlyBillRate) || 0;
          const b = parseFloat(rowB.original.hourlyBillRate) || 0;
          return a - b;
        };

      case "Max Weekly Hours":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.maxWeeklyHours) || 0;
          const b = parseFloat(rowB.original.maxWeeklyHours) || 0;
          return a - b;
        };
        case "Available Weekly Hours":
          return (rowA, rowB, columnId) => {
            const a = parseFloat(rowA.original.availableWeeklyHours) || 0;
            const b = parseFloat(rowB.original.availableWeeklyHours) || 0;
            return a - b;
          };
      case "Operational Capacity":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.operationalCapacity) || 0;
          const b = parseFloat(rowB.original.operationalCapacity) || 0;
          return a - b;
        };

      case "Project Capacity":
        return (rowA, rowB, columnId) => {
          const a = parseFloat(rowA.original.projectCapacity) || 0;
          const b = parseFloat(rowB.original.projectCapacity) || 0;
          return a - b;
        };

      case "Type":
        return (rowA, rowB, columnId) => {
          const a = rowA.original.isExternal ? 1 : 0; // External after Internal
          const b = rowB.original.isExternal ? 1 : 0;
          return a - b;
        };

      case "Creation Date":
        return (rowA, rowB, columnId) => {
          const a = new Date(rowA.original.dateCreated || 0);
          const b = new Date(rowB.original.dateCreated || 0);
          return a.getTime() - b.getTime();
        };

      case "Status":
        return (rowA, rowB, columnId) => {
          const a = rowA.original.isActive ? 0 : 1; // Active first
          const b = rowB.original.isActive ? 0 : 1;
          return a - b;
        };

      default:
        return "alphanumeric";
    }
  };

  const handleRedirectClient = (item) => {
    navigateTo("Clients", item.id, item.name);
  };

  const getSortDescFirst = (col) => {
    const descFirstColumns = [
      "Hourly Cost Rate",
      "Hourly Bill Rate",
      "Max Weekly Hours",
      "Available Weekly Hours",
      "Operational Capacity",
      "Project Capacity",
      "Creation Date",
    ];
    return descFirstColumns.includes(col.label);
  };

  useEffect(() => {
    if (!colsList?.length && !rowData.length) return;

    const baseRows = [...rowData];

    if (isFetchingMore && pagination?.HasNext) {
      const skeletonRows = createSkeletonRows();
      setRows([...baseRows, ...skeletonRows]);
    } else {
      setRows(baseRows);
    }
  }, [rowData, isFetchingMore, pagination?.HasNext, colsList]);

  useEffect(() => {
    if (!colsList?.length && !rowData.length) return;

    const columnsOrder = colsList
      .filter((col) => col.isVisible !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((item) => item.accessorKey);
    setColumnOrder(columnsOrder);

    const columns = colsList
      .filter((col) => col.isVisible !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((col, index) => {
        return {
          Label: col.label,
          CustomName: col.customName,
          Order: col.order,
          Visibility: col.isVisible,
          accessorKey: col.accessorKey,
          header: col.header,
          enableHiding: true,
          enableColumnActions: false,
          grow: true,
          enableResizing: true,
          enableEditing: false,
          size: col.size,
          editVariant: col.columnType,
          enableGlobalFilter: true,
          enableSorting: editingHeader === col.accessorKey ? false : true,
          sortingFn: getSortingFunction(col),
          sortDescFirst: getSortDescFirst(col),
          muiEditTextFieldProps: ({ cell, row }) => ({
            sx: {
              fontFamily: "Figtree-Regular",
              fontSize: "14px",
              padding: "0.5rem",
              maxHeight: "28px",
            },
            required: col.required || false,
            error: !!validationErrors?.[`${row.id}_${cell.column.id}`],
            helperText: validationErrors?.[`${row.id}_${cell.column.id}`],
            onFocus: () =>
              setValidationErrors({
                ...validationErrors,
                [cell.column.id]: undefined,
              }),
            inputRef: (el) => {
              if (col.columnType === "select") {
                selectRefs.current[`${row.id}_${cell.column.id}`] = el;
              }
            },
            onChange: (event) => {
              const newValue = event.target.value;
              setEditedRows((prev) => ({
                ...prev,
                [row.id]: {
                  ...prev[row.id],
                  [cell.column.id]: newValue,
                },
              }));
            },
            onBlur: (event) => {
              const value = event.target.value;
              const validationError =
                col.required && !value ? "Required" : undefined;
              setValidationErrors((prev) => ({
                ...prev,
                [`${row.id}_${cell.column.id}`]: validationError,
              }));
            },
          }),
          Cell: ({ cell, row }) => {
            const item = row.original;
            // Render skeleton for skeleton rows
            if (item.isSkeleton) {
              return (
                <div
                  style={{
                    height: '20px',
                    background: 'linear-gradient(90deg, #e0e0e0 25%, #ECE7FD 50%, #e0e0e0 75%)',
                    backgroundSize: '200% 100%',
                    animation: 'shimmer 1.5s infinite',
                    borderRadius: '4px',
                    width: '100%'
                  }}
                />
              );
            }
            switch (col.label) {
              case "Name":
                return (
                  <div className="d-flex justify-content-between align-items-center inputWidth">
                    <div className="d-flex align-items-center inputWidth">
                      <Tooltip
                        positioning="before"
                        content={{
                          children: item.isFlagged ? "Unflag" : "Flag",
                          className: styles.tooltip,
                        }}
                        relationship="label"
                        withArrow
                      >
                        <span
                          className={`flag ${item.isFlagged ? "flagActive" : ""
                            }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleFlag(item.id, !item.isFlagged);
                          }}
                        >
                          <FlagIcon />
                        </span>
                      </Tooltip>

                      <div className="d-flex justify-content-between gap-2">
                        <HoverCard
                          HoverCardTrigger={
                            <Avatar
                              color="colorful"
                              name={item.fullName}
                              image={{ src: item.profilePictureUrl }}
                              size={24}
                            />
                          }
                          AvatarId={item.id}
                          AvatarName={item.fullName}
                          AvatarDesignation={item.title}
                          AvatarImage={item.profilePictureUrl}
                          AvatarEmail={item.email}
                          AvatarPhone={item.phoneNumber}
                          AvatarIsExternal={item.isExternal}
                        />
                        <span
                          className="d-flex w-full text-truncate items-center"
                          onClick={(e) => {
                            if (getEditPermission("Resource")) {
                              e.stopPropagation();
                              e.preventDefault();
                              handleEdit(item);
                              handlePopoverChange(
                                item.id,
                                !popoverStates[item.id]
                              );
                            }
                          }}
                        >
                          {item.isPendingDelete && (
                            <div className="mr-1">
                              <PendingDeleteIcon />
                            </div>
                          )}
                          {item.fullName}
                        </span>
                      </div>

                      <div className="meatballs-trigger">
                        <Popover
                          open={popoverStates[item.id]}
                          onOpenChange={(e, data) => handlePopoverChange(item.id, data.open || false)}
                          positioning="below-start"
                        >
                          <PopoverTrigger disableButtonEnhancement>
                            <TableCellLayout
                              media={<MeatBallsMenuIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handlePopoverChange(
                                  item.id,
                                  !popoverStates[item.id]
                                );
                              }}
                            />
                          </PopoverTrigger>
                          <PopoverSurface
                            tabIndex={-1}
                            className="listingPopoverWrapper"
                          >
                            <div className="listingPopoverContent">
                              {getEditPermission("Resource") && (
                                <div
                                  className="popItem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handleEdit(item);
                                    handlePopoverChange(
                                      item.id,
                                      !popoverStates[item.id]
                                    );
                                  }}
                                >
                                  <EditIcon />
                                  <p className="text-sm-400 ms-12">Edit</p>
                                </div>
                              )}

                              {getEditPermission("Resource") && (
                                <div
                                  className="popItem"
                                  onClick={() => updateResourceStatus(item, !item.isActive)}
                                >
                                  <img
                                    src={item.isActive ? Deactivate : Activate}
                                    alt=""
                                  />
                                  <p className="text-sm-400 ms-12">{`${item.isActive
                                    ? "Deactivate resource"
                                    : "Activate resource"
                                    }`}</p>
                                </div>
                              )}
                              {item?.twoFactorEnabled && getEditPermission("Resource") && (
                                <div
                                  className="popItem"
                                  onClick={() => resetResourceMfa(item.id)}
                                >
                                  <ShieldOff size={16} strokeWidth={1.5} />
                                  <p className="text-sm-400 ms-12">{`Reset MFA`}</p>
                                </div>
                              )}
                              <div
                                className="popItem"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleReminder(item);
                                  handlePopoverChange(
                                    item.id,
                                    !popoverStates[item.id]
                                  );
                                }}
                              >
                                <RemindIcon />
                                <p className="text-sm-400 ms-12">Remind me</p>
                              </div>
                              {getDeletePermission("Resource") && (
                                <div
                                  className="popItem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handlePopoverChange(
                                      item.id,
                                      !popoverStates[item.id]
                                    );
                                    handleDelete(item);
                                  }}
                                >
                                  <DeleteIcon />
                                  <p className="text-sm-400 ms-12">Delete</p>
                                </div>
                              )}
                            </div>
                          </PopoverSurface>
                        </Popover>
                      </div>
                    </div>
                  </div>
                );
              case "Sequence #":
                return formatSequenceNumber("User", item.sequenceNumber);
              case "Clients":
                return (

                  item.clients?.length > 0 ?
                    (
                      <div className="text-truncate d-inline">
                        {item.clients.map((client, index) => (
                          <>
                            <span className="hover:underline" key={client.id || index} onClick={() => handleRedirectClient(client)}>
                              {client.name}
                            </span>
                            {index < item.clients.length - 1 && <span>&nbsp;/&nbsp;</span>}
                          </>
                        ))}
                      </div>
                    ) :
                    ''
                );
              case "Email":
                return item.email;
              case "Roles":
                return (
                  item.roles?.length > 0 ?
                    (
                      <div className="text-truncate d-inline">
                        {item.roles.map((role, index) => (
                          <>
                            <span>{role.name}</span>
                            {index < item.roles.length - 1 && <span>&nbsp;/&nbsp;</span>}
                          </>
                        ))}
                      </div>
                    ) :
                    ''
                );
              case "Title":
                return <span>{item.title}</span>;
              case "Hourly Cost Rate":
                return `${getAccountCurrency(setting)}${displayDecimalFormatted(
                    setting,
                    item.hourlyRate
                  )}`;
              case "Hourly Bill Rate":
                return `${getAccountCurrency(setting)}${displayDecimalFormatted(
                    setting,
                    item.hourlyBillRate
                  )}`;
              case "Max Weekly Hours":
                return `${displayDecimalFormatted(setting, item.maxWeeklyHours)}h`;
              case "Available Weekly Hours":
                return `${displayDecimalFormatted(setting, item.availableWeeklyHours)}h`;
              case "Operational Capacity":
                return item.operationalCapacity
                  ? `${displayDecimalFormatted(
                    setting,
                    item.operationalCapacity
                  )}%`
                  : "";
              case "Project Capacity":
                return item.projectCapacity
                  ? `${displayDecimalFormatted(setting, item.projectCapacity)}%`
                  : "";
              case "Type":
                return (
                  <div className="flex w-100 justify-center items-center">
                    {item.isExternal ? (
                      <span className="pill w-100 grey">External</span>
                    ) : (
                      <span className="pill w-100 purple">Internal</span>
                    )}
                  </div>
                );
              case "Creation Date":
                return item.dateCreated
                  ? utcToLocalDateString(setting, item.dateCreated)
                  : "";
              case "Status":
                return (
                  <div className="flex w-100 justify-center items-center">
                    {item.isActive ? (
                      <span className="pill w-100 success">Active</span>
                    ) : (
                      <span className="pill w-100 warning">Inactive</span>
                    )}
                  </div>
                );
              default:
                return cell.getValue();
            }
          },
          Edit: ({ cell, row, table }) => {
            const value =
              editedRows[row.id]?.[cell.column.id] ??
              cell.getValue() ??
              row.original[cell.column.id] ??
              (col.columnType === "select" ? null : "");
            return (
              <EditInput
                label={col.label}
                value={value}
                onChange={(newValue) => {
                  if (row.id.startsWith("mrt-row-create")) {
                    table.setCreatingRow((prev) => ({
                      ...prev,
                      values: {
                        ...prev.values,
                        [cell.column.id]: newValue,
                      },
                    }));
                  } else {
                    setEditedRows((prev) => ({
                      ...prev,
                      [row.id]: {
                        ...prev[row.id],
                        [cell.column.id]: newValue,
                      },
                    }));
                  }
                }}
                onBlur={() => {
                  if (!row.id.startsWith("mrt-row-create")) {
                    const newValue = editedRows[row.id]?.[cell.column.id];
                    table.options.onEditingCellSave?.({
                      cell,
                      value: newValue,
                      row,
                      table,
                    });
                    table.setEditingCell(null);
                  }
                }}
                type={col.columnType}
                dateFormat={
                  col.columnType === "date"
                    ? setting.regionalSettings.dateFormat
                    : null
                }
                programId={row.original?.program?.id}
                portfolioId={row.original?.portfolio?.id}
                parentType={parentType}
                table={table}
              />
            );
          },
          Header: ({ column }) => {
            return editingHeader === column.id ? (
              <input
                value={headerValue}
                onChange={(e) => setHeaderValue(e.target.value)}
                onBlur={() => handleHeaderSave(column.id)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleHeaderSave(column.id);
                  }
                }}
                autoFocus
                className="header-edit-input"
                style={{
                  minWidth: column.getSize() - 60,
                }}
              />
            ) : (
              <span>{column.columnDef.header}</span>
            );
          },
        };
      });
    setCols((prevCols) => {
      if (JSON.stringify(prevCols) !== JSON.stringify(columns)) {
        return columns;
      }
      return prevCols;
    });

  }, [colsList, rowData, editingHeader, headerValue, isFetchingMore]);


  const updateResourceStatus = (resource, newStatus) => {
    const resourceId = resource.id;
    setConfirmationModalData({
      action: "updateStatus",
      title: newStatus ? "Activate Resource" : "Deactivate Resource",
      body: `Are you sure you want to ${newStatus ? "activate" : "deactivate"} this resource?`,
      saveBtnLabel: newStatus ? "Activate" : "Deactivate",
      cancelBtnLabel: "Cancel",
      isLoading: false,
      resourceId: resourceId,
      newStatus: newStatus,
      checkboxOptions: newStatus ?
        [
          {
            id: 'IsResetPassword',
            text: `Reset access password and notify the user by email (${resource.email || 'no email available'})`,
            defaultChecked: resource.email ? true : false,
            isDisabled: resource.email ? false : true
          },
        ]
        : null
    })
    handlePopoverChange(
      resourceId,
      !popoverStates[resourceId]
    );
  }

  const resetResourceMfa = (resourceId) => {
    setConfirmationModalData({
      action: "resetMfa",
      title: "Disable MFA",
      body: `Are you sure you want to disable MFA for this resource?`,
      saveBtnLabel: "Disable",
      cancelBtnLabel: "Cancel",
      isLoading: false,
      resourceId: resourceId
    })
    handlePopoverChange(
      resourceId,
      !popoverStates[resourceId]
    );
  }

  const handeCancel = () => {
    setConfirmationModalData(null);
  }
  const handleConfirm = async (selectedIds, selectedOptions) => {

    try {
      setConfirmationModalData((prev) => ({ ...prev, isLoading: true }))
      if (confirmationModalData?.action === "updateStatus") {
        var payload = {
          id: confirmationModalData.resourceId,
          payload: {
            isActive: confirmationModalData.newStatus,
            ...(selectedOptions && selectedOptions)
          }
        };

        await userService
          .updateUserStatus(payload)
          .then((res) => {
            if (res.success) {
              dispatch(
                generalActions.showToast({
                  title: res.message || "Resource status updated successfully",
                  type: "success",
                })
              );
              handleRefresh();
            }
            else {
              dispatch(
                generalActions.showToast({
                  title: res.message || "Something went wrong. Please try again later.",
                  type: "error",
                })
              );
            }
          });

      }
      else if (confirmationModalData?.action === "resetMfa") {

        const result = await mfaService.resetUserMfa(confirmationModalData?.resourceId);
        if (result.success) {
          dispatch(
            generalActions.showToast({
              title: result.message,
              type: "success",
            })
          );
          handleRefresh();
        } else {
          dispatch(
            generalActions.showToast({
              title: result.error || "Something went wrong. Please try again later.",
              type: "error",
            })
          );
        }
      }
    }
    catch (error) {
      dispatch(
        generalActions.showToast({
          title: error?.message || "Something went wrong. Please try again later.",
          type: "error",
        })
      );
    }
    finally {
      setConfirmationModalData(null);
    }
  }

  const table = useMaterialReactTable({
    columns: cols,
    data: rows,
    enableHiding: true,
    enableSorting: true,
    manualSorting: false,
    visibleInShowHideMenu: false,
    enablePagination: false,
    enableTopToolbar: true,
    enableGlobalFilter: true,
    enableStickyHeader: true,
    enableColumnFilterModes: true,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    editDisplayMode: "cell",
    globalFilterFn: "contains",
    enableEditing: false,
    enableFilters: false,
    enableCreatingRow: true,
    createDisplayMode: "row",
    positionCreatingRow: "bottom",
    initialState: {
      density: "compact",
      showGlobalFilter: false,
      showColumnFilters: false,
      enableHiding: false,
      columnOrder: cols.map((col) => col.order),
    },
    state: {
      sorting,
      isLoading: rows.length === 0 && resourceLoading,
      columnSizing,
      columnOrder,
      showLoadingOverlay: false,
      showProgressBars: false
    },
    onSortingChange: setSorting,
    onEditingRowCancel: () => {
      setValidationErrors({});
      setEditedRows({});
    },
    positionGlobalFilter: "left",
    onEditingCellSave: ({ cell, row }) => {
      const editedRow = editedRows[row.id];
      const newValue =
        editedRow?.[cell.column.id] !== undefined
          ? editedRow[cell.column.id]
          : cell.getValue();

      if (newValue === cell.getValue()) return; // Skip if no real change
      setRows((prevRows) =>
        prevRows.map((r, index) => {
          if (index === row.index) {
            return { ...r, [cell.column.id]: newValue };
          }
          return r;
        })
      );

      // Remove the specific cell edit
      setEditedRows((prev) => {
        const updated = { ...prev };
        if (updated[row.id]) {
          delete updated[row.id][cell.column.id];
          if (Object.keys(updated[row.id]).length === 0) {
            delete updated[row.id];
          }
        }
        return updated;
      });

      const prePayload = {
        ...row.original,
        [cell.column.id]: newValue,
      };
      const payload = formatPayload(prePayload);
      handleSave(row.original.id, {
        ...payload,
      });
    },
    onColumnSizingChange: (updater) => {
      setColumnSizing((prev) => {
        const newSizing =
          typeof updater === "function" ? updater(prev) : updater;
        Object.entries(newSizing).forEach(([columnId, width]) => {
          setColumnWidth(tableName, columnId, width);
        });
        return newSizing;
      });
    },
    onColumnOrderChange: (updater) => {
      const newOrder = typeof updater === "function" ? updater() : updater;
      const neworderList = newOrder
        .filter((item) => item !== "mrt-row-spacer")
        .filter(Boolean);
      const areEqual = compareArrays(neworderList, columnOrder);
      if (areEqual) {
        return newOrder;
      }

      const sortedColumns = sortColumnsByOrderlist(colsList, neworderList);
      handleColumnOrdering(sortedColumns);
      setColumnOrder(neworderList);
      return neworderList;
    },
    muiTableHeadCellProps: ({ column }) => ({
      sx: {
        fontFamily: "Figtree-Medium",
        fontSize: "14px",
        padding: "1rem 0.5rem 1rem 1rem",
        backgroundColor: "#f8f7fc",
        color:"#342c82",
        height: "40px",
        fontWeight: 600,
        "&:hover": {
          backgroundColor: "#ece8fd",
          "& .MuiDivider-root": {
            opacity: 1,
          },
          "& .MuiButtonBase-root": {
            opacity: 1,
          },
          fontWeight: 700,
        },
        position: "relative",
        "& .Mui-TableHeadCell-Content": {
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          height: "100%",
        },
        "& .MuiTableSortLabel-root": {
          marginLeft: "8px",
          paddingTop: "3px",
          opacity: 0,
          "&:hover": {
            opacity: 1,
          },
        },
        "& .MuiDivider-root": {
          cursor: "col-resize",
          backgroundColor: "#342C82",
          opacity: 0,
        },
        "& .MuiButtonBase-root": {
          cursor: "grab",
          opacity: 0,
          "& .MuiSvgIcon-root": {
            path:{
              fill: "#342C82"
            }
          }
        },
      },
      onDoubleClick: (e) => {
        e.stopPropagation();
        e.preventDefault();
        handleHeaderDoubleClick(column.id, column.columnDef.header);
      },
    }),
    muiTopToolbarProps: {
      showColumnFilters: false,
      showGlobalFilter: true,
      enableHiding: false,
      sx: {
        position: "sticky",
        top: 0,
        zIndex: 1,
        "& .MuiBox-root": {
          padding: 0,
          display: "flex",
          alignItems: "center",
        },
      },
    },
    renderTopToolbar: () => (
      <ListingHeader
        isHeader={true}
        parentType="Resource"
        type="Resource"
        btnLable="Add Resource"
        searchedData={searchHandler}
        setIsCreate={() => setIsCreate(true)}
        rightPane={true}
        isCreatable={false}
        isSearchDisable={cols.length}
        handleCreateFunc={() => {
          handleAddRow();
          table.setCreatingRow(true);
        }}
        triggerSearch={triggerSearch}
      />
    ),
    renderBottomToolbarCustomActions: () => (
      <div className="bottom-toolbar-container">
        <div className="toolbar-content">
          {resourceLoading || isFetchingMore ? (
            <div className="loading-indicator">
              <span>{`${!isFetchingMore ? 'Loading...' : 'Loading more data...'}`}</span>
            </div>
          ) : pagination?.TotalCount > 0 ? (
            <div className="data-info">
              <span className="data-text">
                Showing <strong>{rowData.length}</strong> of{" "}
                <strong>{pagination?.TotalCount || 0}</strong> total records
              </span>
            </div>
          ) : (
            <div className="data-info">
              <span className="data-text">No records Found</span>
            </div>
          )}
        </div>
      </div>
    ),
    muiBottomToolbarProps: {
      sx: {
        position: "absolute",
        bottom: 0,
        backgroundColor: "#f8f7fc",
        padding: "0.7rem 1rem",
        width: "100%",
        height: "38px",
      },
    },
    muiTableBodyProps: {
      sx: {
        fontFamily: "Figtree-Regular",
        fontSize: "13px",

        marginBottom: "66px",
        "& .MuiTableBody-Cell": {
          "&:hover": {
            backgroundColor: "#ece8fd15",
          },
        },
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        height: "32px",
        position: "relative",
        "&:hover": {
          backgroundColor: row.original.isSkeleton ? "transparent" : "#ece8fd15",
        },
        cursor: row.original.isSkeleton ? "default" : "pointer",
      },
      className: row.original.isSkeleton ? "" : "hoverable-row",
    }),
    muiTableContainerProps: ({ table }) => ({
      ref: tableContainerRef,
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight || parentType
          ? 115
          : 70
          }px)`,
        overflow: "auto",
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "4px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "#FFFFFF",
          borderRadius: "8px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#342c82",
          borderRadius: "8px",
          "&:hover": {
            backgroundColor: "#342c82",
          },
        },
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    muiSkeletonProps: {
      height: 34,
      sx: {
        background:
          "linear-gradient(90deg, #e0e0e0 25%, #ECE7FD 50%, #e0e0e0 75%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.5s infinite",
        "@keyframes shimmer": {
          "0%": {
            backgroundPosition: "200% 0",
          },
          "100%": {
            backgroundPosition: "-200% 0",
          },
        },
      },
    },
    muiTableBodyCellProps: ({ cell, column, table }) => ({
      sx: {
        fontFamily: "Figtree-Regular",
        fontSize: "13px",
        padding: "0.5rem 1rem",
        position: "relative",
        overflow: "visible",
        "&:focus": {
          outline: "none",
        },
        "&:hover": {
          fontFamily: "Figtree-Medium",
          fontWeight: "bold",
        },
      },
    }),
    muiLinearProgressProps: {
      sx: {
        height: "4px",
        backgroundColor: "#cfc9ff",
        "& .MuiLinearProgress-bar": {
          backgroundColor: "#342c82",
        },
        "& .MuiLinearProgress-bar1Indeterminate": {
          backgroundColor: "#342c82",
        },
        "& .MuiLinearProgress-bar2Indeterminate": {
          backgroundColor: "#342c82",
        },
        "& .MuiLinearProgress-barColorPrimary": {
          backgroundColor: "#342c82",
        },
        "& .MuiLinearProgress-colorPrimary": {
          backgroundColor: "#cfc9ff",
        },
        // Override any secondary color variants
        "& .MuiLinearProgress-colorSecondary": {
          backgroundColor: "#cfc9ff",
        },
        "& .MuiLinearProgress-barColorSecondary": {
          backgroundColor: "#342c82",
        },
      },
    },
    renderEmptyRowsFallback: () => {
      return resourceLoading ? (
        <div className="d-flex justify-content-center">
          <Spin />
        </div>
      ) : rows.length === 0 ? (
        <div className="flex-grow flex items-center justify-center p-4">
          <div className="h-full flex flex-col items-center justify-center min-h-[400px] p-8">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="65"
              height="64"
              viewBox="0 0 65 64"
              fill="none"
              className="mb-4"
            >
              <path
                d="M6.9002 21.6008C6.9002 16.7407 10.8401 12.8008 15.7002 12.8008H49.3002C54.1603 12.8008 58.1002 16.7407 58.1002 21.6008V42.4008C58.1002 47.2609 54.1603 51.2008 49.3002 51.2008H32.8884L27.8968 46.2092C28.7964 44.3293 29.3002 42.2239 29.3002 40.0008C29.3002 32.0479 22.8531 25.6008 14.9002 25.6008C11.94 25.6008 9.18835 26.494 6.9002 28.0257V21.6008ZM14.9002 51.2008C17.416 51.2008 19.7381 50.3713 21.6079 48.9708L29.7688 57.132C30.3936 57.7569 31.4067 57.7569 32.0315 57.1321C32.6564 56.5072 32.6564 55.4942 32.0316 54.8693L23.8706 46.708C25.2708 44.8384 26.1002 42.5164 26.1002 40.0008C26.1002 33.8152 21.0858 28.8008 14.9002 28.8008C8.71461 28.8008 3.7002 33.8152 3.7002 40.0008C3.7002 46.1864 8.71461 51.2008 14.9002 51.2008ZM14.9002 48.0008C10.4819 48.0008 6.9002 44.4191 6.9002 40.0008C6.9002 35.5825 10.4819 32.0008 14.9002 32.0008C19.3185 32.0008 22.9002 35.5825 22.9002 40.0008C22.9002 44.4191 19.3185 48.0008 14.9002 48.0008Z"
                fill="#342C82"
              />
            </svg>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              No data currently available!
            </h3>
            <p className="text-gray-600 text-center max-w-md">
              New entries will appear here once created.
            </p>
          </div>
        </div>
      ) : null;
    },
  });

  return (
    <>

      <MaterialReactTable table={table} />

      {confirmationModalData && (
        <Modal
          title={confirmationModalData?.title || "Confirm Action"}
          body={confirmationModalData?.body || "Are you sure you want to proceed?"}
          handleCancel={() => handeCancel()}
          handleSave={handleConfirm}
          onConfirmWithOptions={handleConfirm}
          saveBtnLabel={confirmationModalData?.saveBtnLabel || "Confirm"}
          cancelBtnLabel={confirmationModalData?.cancelBtnLabel || "Cancel"}
          isLoading={confirmationModalData?.isLoading || false}
          checkboxOptions={confirmationModalData?.checkboxOptions}
        />
      )}
    </>

  );
};

export default ResourceTable;
